import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { AttendanceCodeMap, fetchActiveAttendanceCodes } from '../services/attendanceCode.service';
import { hasAuthToken } from '../services/api.client';

// ── Context shape ─────────────────────────────────────────────────────────

export interface AttendanceCodeContextValue {
  /** Map of attendance type name to its configured code string */
  codes: AttendanceCodeMap;
  /** True while the initial fetch is in progress */
  loading: boolean;
  /** True once codes have been successfully fetched from the API */
  codesLoaded: boolean;
  /** Force a re-fetch (e.g. after admin changes codes) */
  refresh: () => void;
}

export const AttendanceCodeContext = createContext<AttendanceCodeContextValue>({
  codes: {} as AttendanceCodeMap,
  loading: true,
  codesLoaded: false,
  refresh: () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────

export const AttendanceCodeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [codes, setCodes] = useState<AttendanceCodeMap>({} as AttendanceCodeMap);
  const [loading, setLoading] = useState(true);
  const [codesLoaded, setCodesLoaded] = useState(false);

  const load = useCallback(async () => {
    // Nothing to load without a session. This provider sits above the whole app, so on the login
    // page it was asking for the company's attendance codes with no token and logging a 401 on
    // every visit — a failure the console reports and nobody can act on. The auth-changed listener
    // below fetches the moment a session exists.
    if (!hasAuthToken()) { setCodesLoaded(false); setLoading(false); return; }

    setLoading(true);
    try {
      const data = await fetchActiveAttendanceCodes();
      setCodes(data);
      setCodesLoaded(true);
    } catch {
      // API unavailable or unauthenticated — codes remain empty until next successful fetch
      setCodesLoaded(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    load();
  }, [load]);

  // Re-fetch when auth changes (login/logout or branch switch)
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener('auth-changed', handler);
    return () => window.removeEventListener('auth-changed', handler);
  }, [load]);

  const value = useMemo<AttendanceCodeContextValue>(
    () => ({ codes, loading, codesLoaded, refresh: load }),
    [codes, loading, codesLoaded, load],
  );

  return (
    <AttendanceCodeContext.Provider value={value}>
      {children}
    </AttendanceCodeContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────

export function useAttendanceCodes(): AttendanceCodeContextValue {
  return useContext(AttendanceCodeContext);
}
